import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import usePendingBookings from '../../hooks/usePendingBookings';
import { generateUniqueColor } from '../../util/colorUtil';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import MarkNoShowButton from '../Bookings/components/MarkNoShowButton';
import NoShowInvoiceButton from '../Bookings/components/NoShowInvoiceButton';
import RevertNoShowButton from '../Bookings/components/RevertNoShowButton';
import SkipNoShowInvoiceButton from '../Bookings/components/SkipNoShowInvoiceButton';
import RetroactiveCompleteButton from '../Bookings/components/RetroactiveCompleteButton';
import ResolveForgottenPaymentButton from '../Bookings/components/ResolveForgottenPaymentButton';
import UnpaidBookingInvoiceButton from '../Bookings/components/UnpaidBookingInvoiceButton';
import SkipUnpaidBookingInvoiceButton from '../Bookings/components/SkipUnpaidBookingInvoiceButton';
import BulkAcknowledgeAutoCheckedOutButton from '../Bookings/components/BulkAcknowledgeAutoCheckedOutButton';
import ForgottenPaymentInvoiceButton from '../Bookings/components/ForgottenPaymentInvoiceButton';
import LockIconWrapper from '../components/LockIconWrapper';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { currencyFormatter } from '../../util/formatHelper';

const MENU_ITEM_NAME = 'Handlungsbedarf';

// Escalation thresholds for an unanswered booking request, in minutes. A request is a
// customer actively waiting for a reply, so the window is deliberately short. Kept in
// step with the leading frontend's src/features/pending/waiting-time.ts.
const WAITING_WARNING_MINUTES = 5;
const WAITING_CRITICAL_MINUTES = 15;

export default function PendingBookingsView() {
  const authCtx = useContext(AuthContext);
  const {
    fetchPendingBookings,
    confirmPendingBooking,
    rejectPendingBooking,
    markCustomerCommentHandled,
    acknowledgeFailedRefund,
  } = usePendingBookings();
  const PAGE_SIZE = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [pageBySectionKey, setPageBySectionKey] = useState({});
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [showAll, setShowAll] = useState(false);
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    bookingId: null,
    borderColor: null,
  });

  if (!authCtx.checkRightAny('internalBooking:read:own')) {
    return <Navigate to='/welcome' replace />;
  }

  const hasConfirmPendingRight = authCtx.checkRightAny('internalBooking:special:confirmpending');
  const hasAttentionShowAllRight = authCtx.checkRightAny(
    'internalBooking:special:attentionShowAll'
  );
  const hasHandleHintsRight = authCtx.checkRightAny(
    'internalBooking:special:handleCustomerComments'
  );
  const hasAcknowledgeRefundRight = authCtx.checkRightAny(
    'payment:special:acknowledgeFailedRefunds'
  );

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // How long an unanswered booking request has been waiting. A booking is created
  // with the status awaitingConfirmation and never moves back into it, so the
  // creation time is the moment the customer started waiting. The label wording and
  // the escalation thresholds match the leading frontend's "Wartet seit" column.
  const getWaitingMeta = booking => {
    const createdAtRaw = booking?.meta?.createdAt || booking?.eventCreatedAt || booking?.createdAt;
    const createdAtTs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN;
    if (!Number.isFinite(createdAtTs)) return null;

    const diffMs = Math.max(0, nowTs - createdAtTs);
    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let label;
    if (totalMinutes < 1) {
      label = 'gerade eben';
    } else if (days > 0) {
      const dayPart = `${days} ${days === 1 ? 'Tag' : 'Tage'}`;
      label = hours > 0 ? `${dayPart} ${hours} Std.` : dayPart;
    } else if (hours > 0) {
      label = minutes > 0 ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
    } else {
      label = `${minutes} Min.`;
    }

    let color = 'success';
    if (totalMinutes >= WAITING_CRITICAL_MINUTES) color = 'error';
    else if (totalMinutes >= WAITING_WARNING_MINUTES) color = 'warning';

    return { label, color, totalMinutes, since: new Date(createdAtTs) };
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await fetchPendingBookings({
        showAll: hasAttentionShowAllRight && showAll,
      });
      const nextSections = res?.sections || [];
      setSections(nextSections);
      setPageBySectionKey(prev => {
        const next = { ...prev };
        for (const section of nextSections) {
          const key = section?.key;
          if (!key) continue;
          const total = Array.isArray(section?.results) ? section.results.length : 0;
          const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
          const current = Number.isFinite(prev?.[key]) ? prev[key] : 0;
          next[key] = Math.min(Math.max(0, current), maxPage);
        }
        return next;
      });
      authCtx.refreshCountForMenuItem && authCtx.refreshCountForMenuItem(MENU_ITEM_NAME);
    } catch (_) {
      // Errors are handled via toast in the hook.
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const handleOpenDetails = booking => {
    if (!booking || !booking._id) return;
    const color = booking.resourceId?._id
      ? generateUniqueColor(String(booking.resourceId._id))
      : generateUniqueColor(String(booking._id));
    setDetailsModal({ open: true, bookingId: String(booking._id), borderColor: color });
  };

  const handleCloseDetails = () => {
    setDetailsModal({ open: false, bookingId: null, borderColor: null });
  };

  const handleDetailsUpdate = (bookingId, updated) => {
    if (!bookingId || !updated) return;
    load();
  };

  // School classes are booked on their own products ("Lasertag Schulausflug",
  // "Bashgame Schulklasse", …), which always require confirmation and therefore always
  // land in this list. Their booking is a different job than an ordinary group booking —
  // it has to be checked, not just confirmed — so the row says so instead of hiding it
  // behind the product name inside the booking. The occasion is resolved by the backend.
  const renderSchoolClassChip = booking => {
    if (booking?.occasion !== 'schoolTrip') return null;
    return (
      <Tooltip title='Buchung auf einem Schulklassen-Tarif'>
        <Chip
          size='small'
          icon={<SchoolIcon />}
          label='Schulklasse'
          color='info'
          sx={{ fontWeight: 700 }}
        />
      </Tooltip>
    );
  };

  const renderAttentionChip = booking => {
    switch (booking?.attentionCategory) {
      case 'awaitingConfirmation': {
        const waiting = getWaitingMeta(booking);
        const chip = (
          <Chip
            size='small'
            label={waiting ? `Anfrage offen: ${waiting.label}` : 'Anfrage offen'}
            color={waiting?.color || 'warning'}
            sx={{ fontWeight: 700 }}
          />
        );
        if (!waiting) return chip;
        return (
          <Tooltip
            title={`Anfrage eingegangen am ${dayjs(waiting.since).format('DD.MM.YYYY HH:mm')} Uhr`}
          >
            <span>{chip}</span>
          </Tooltip>
        );
      }
      case 'waitingForPayment':
        return (
          <Chip size='small' label='Wartet auf Zahlung' color='info' sx={{ fontWeight: 700 }} />
        );
      case 'missingCheckIn':
        return (
          <Chip
            size='small'
            label='Seit Vortag nicht eingecheckt'
            color='warning'
            sx={{ fontWeight: 700 }}
          />
        );
      case 'missingCheckOut':
        return (
          <Chip
            size='small'
            label='Auschecken vergessen'
            color='warning'
            sx={{ fontWeight: 700 }}
          />
        );
      case 'forgottenPayment':
        return (
          <Chip
            size='small'
            label='Zahlung nicht erfasst'
            color='warning'
            sx={{ fontWeight: 700 }}
          />
        );
      case 'autoCheckedOut':
        return (
          <Chip
            size='small'
            label='Info: automatisch ausgecheckt'
            color='info'
            sx={{ fontWeight: 700 }}
          />
        );
      case 'noShow':
        return <Chip size='small' label='No-Show' color='error' sx={{ fontWeight: 700 }} />;
      case 'retainedInsurance':
        return (
          <Chip
            size='small'
            label='Offene Ticketversicherung'
            color='error'
            sx={{ fontWeight: 700 }}
          />
        );
      case 'customerHints':
        return (
          <Chip size='small' label='Kundenhinweis' color='secondary' sx={{ fontWeight: 700 }} />
        );
      default:
        return (
          <Chip size='small' label='Handlungsbedarf' color='default' sx={{ fontWeight: 700 }} />
        );
    }
  };

  const handleReject = async booking => {
    const toastMessage = `Die Ablehnungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
    const successToastMessage = 'Buchung abgelehnt';
    const displayInfo = {
      Typ: 'Buchungsanfrage ablehnen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
      Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
    };
    const onSuccess = () => {
      toast.success(successToastMessage);
      load();
    };
    try {
      await rejectPendingBooking(
        booking._id,
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
    } catch (_) {}
  };

  const handleConfirm = async booking => {
    const toastMessage = `Die Bestätigungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
    const successToastMessage = 'Buchung bestätigt';
    const displayInfo = {
      Typ: 'Buchungsanfrage bestätigen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
      Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
    };
    const onSuccess = () => {
      toast.success(successToastMessage);
      load();
    };
    try {
      await confirmPendingBooking(
        booking._id,
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
    } catch (_) {}
  };

  const handleMarkHintRead = async booking => {
    try {
      await markCustomerCommentHandled(booking._id, true);
      toast.success('Kundenhinweis als gelesen markiert');
      load();
    } catch (_) {
      // Errors are surfaced via toast in the hook.
    }
  };

  const handleMarkRefundRead = async item => {
    try {
      await acknowledgeFailedRefund(item.paymentId, item.refundId, true);
      toast.success('Rückerstattung als gelesen markiert');
      load();
    } catch (_) {
      // Errors are surfaced via toast in the hook.
    }
  };

  const actionButtonSx = { whiteSpace: 'nowrap' };

  const renderRowActions = booking => {
    const category = booking?.attentionCategory;

    const detailsButton = (
      <Tooltip title='Details öffnen' key='details'>
        <IconButton size='small' onClick={() => handleOpenDetails(booking)} sx={{ p: 0.5 }}>
          <OpenInNewIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    );

    const actions = [detailsButton];

    if (category === 'awaitingConfirmation' && hasConfirmPendingRight) {
      actions.push(
        <Button
          key='confirm'
          size='small'
          variant='contained'
          color='success'
          onClick={() => handleConfirm(booking)}
          sx={actionButtonSx}
        >
          <LockIconWrapper right='internalBooking:special:confirmpending'>
            Bestätigen
          </LockIconWrapper>
        </Button>,
        <Button
          key='reject'
          size='small'
          variant='outlined'
          color='error'
          onClick={() => handleReject(booking)}
          sx={actionButtonSx}
        >
          <LockIconWrapper right='internalBooking:special:confirmpending'>
            Ablehnen
          </LockIconWrapper>
        </Button>
      );
    }

    if (category === 'missingCheckIn') {
      actions.push(
        <MarkNoShowButton
          key='noShow'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />,
        <RetroactiveCompleteButton
          key='retroactive'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          variant='contained'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    if (category === 'forgottenPayment') {
      actions.push(
        <ForgottenPaymentInvoiceButton
          key='forgottenPaymentInvoice'
          booking={booking}
          onAfterSend={load}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />,
        <ResolveForgottenPaymentButton
          key='resolveForgottenPayment'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          variant='contained'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    if (category === 'noShow') {
      actions.push(
        <RevertNoShowButton
          key='revertNoShow'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />,
        <NoShowInvoiceButton
          key='noShowInvoice'
          booking={booking}
          onAfterSend={load}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />,
        <SkipNoShowInvoiceButton
          key='skipNoShowInvoice'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          onAfterSkip={load}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    if (category === 'retainedInsurance') {
      actions.push(
        <UnpaidBookingInvoiceButton
          key='unpaidBookingInvoice'
          booking={booking}
          onAfterSend={load}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />,
        <SkipUnpaidBookingInvoiceButton
          key='skipUnpaidBookingInvoice'
          booking={booking}
          onBookingUpdated={handleDetailsUpdate}
          onAfterSkip={load}
          variant='outlined'
          size='small'
          sx={actionButtonSx}
        />
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 0.75, sm: 1 },
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', sm: 'center' },
          minWidth: { sm: 'max-content' },
        }}
      >
        {actions}
      </Box>
    );
  };

  const [expandedAutoCheckoutBranches, setExpandedAutoCheckoutBranches] = useState({});

  const toggleAutoCheckoutBranch = branchId => {
    setExpandedAutoCheckoutBranches(prev => ({ ...prev, [branchId]: !prev[branchId] }));
  };

  const getArrivalResponsible = booking => {
    if (!Array.isArray(booking?.onSiteProtocol)) return null;
    const arrival = booking.onSiteProtocol.find(entry => entry?.type === 'arrival');
    if (!arrival) return null;
    return arrival.createdByString || arrival.createdByUsername || null;
  };

  const renderAutoCheckedOutSection = section => {
    const rows = Array.isArray(section?.results) ? section.results : [];
    if (rows.length === 0) return null;

    const groupsByBranch = new Map();
    for (const booking of rows) {
      const branchId = String(booking?.branchId?._id || 'unknown');
      const branchName = booking?.branchId?.locationName || 'Unbekannter Standort';
      if (!groupsByBranch.has(branchId)) {
        groupsByBranch.set(branchId, { branchId, branchName, bookings: [] });
      }
      groupsByBranch.get(branchId).bookings.push(booking);
    }

    const groups = Array.from(groupsByBranch.values()).sort((a, b) =>
      a.branchName.localeCompare(b.branchName, 'de')
    );

    return (
      <Paper key={section.key} sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6'>{section.title}</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            Der Nacht-Cron hat diese Buchungen um 02:00 automatisch ausgecheckt, weil das
            Auschecken vergessen wurde. Bitte pro Standort bestätigen — dabei wird
            nichts an der Buchung geändert, es zählt nur die Rückmeldung der
            Standortleitung, dass das Team nachgeschult wird.
          </Typography>
        </Box>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Standort</TableCell>
                <TableCell align='right'>Automatisch ausgecheckt</TableCell>
                <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(group => {
                const isExpanded = Boolean(expandedAutoCheckoutBranches[group.branchId]);
                return (
                  <React.Fragment key={`${section.key}-${group.branchId}-group`}>
                    <TableRow>
                      <TableCell sx={{ width: 40, pr: 0 }}>
                        <Tooltip title={isExpanded ? 'Einklappen' : 'Buchungen anzeigen'}>
                          <IconButton
                            size='small'
                            onClick={() => toggleAutoCheckoutBranch(group.branchId)}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize='small' />
                            ) : (
                              <KeyboardArrowDownIcon fontSize='small' />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{group.branchName}</TableCell>
                      <TableCell align='right'>
                        <Chip
                          size='small'
                          label={group.bookings.length}
                          color='info'
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: { xs: 0.75, sm: 1 },
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: 'wrap',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            minWidth: { sm: 'max-content' },
                          }}
                        >
                          <BulkAcknowledgeAutoCheckedOutButton
                            branchName={group.branchName}
                            bookings={group.bookings}
                            onResolved={load}
                            variant='contained'
                            size='small'
                            sx={actionButtonSx}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                          <Table size='small' sx={{ bgcolor: 'action.hover' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Nr.</TableCell>
                                <TableCell>Termin</TableCell>
                                <TableCell>Kunde</TableCell>
                                <TableCell>Ressource</TableCell>
                                <TableCell>Verantwortlich (Check-in)</TableCell>
                                <TableCell align='right'>Details</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.bookings.map(booking => {
                                const responsible = getArrivalResponsible(booking);
                                return (
                                  <TableRow key={`expand-booking-${booking._id}`}>
                                    <TableCell>
                                      <Link
                                        component='button'
                                        variant='body2'
                                        onClick={() => handleOpenDetails(booking)}
                                        sx={{ cursor: 'pointer', textDecoration: 'none' }}
                                      >
                                        {booking.bookingNumber}
                                      </Link>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      {dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')}
                                    </TableCell>
                                    <TableCell>
                                      {booking.firstName} {booking.lastName}
                                    </TableCell>
                                    <TableCell>{booking.resourceId?.name || '—'}</TableCell>
                                    <TableCell>
                                      {responsible ? (
                                        <Chip
                                          size='small'
                                          label={responsible}
                                          variant='outlined'
                                          sx={{ fontWeight: 600 }}
                                        />
                                      ) : (
                                        <Typography variant='body2' color='text.disabled'>
                                          Unbekannt
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align='right'>
                                      <Tooltip title='Details öffnen'>
                                        <IconButton
                                          size='small'
                                          onClick={() => handleOpenDetails(booking)}
                                          sx={{ p: 0.5 }}
                                        >
                                          <OpenInNewIcon fontSize='small' />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderFailedRefundSection = section => {
    const rows = Array.isArray(section?.results) ? section.results : [];
    if (rows.length === 0) return null;

    const sectionKey = section.key;
    const page = Number.isFinite(pageBySectionKey?.[sectionKey]) ? pageBySectionKey[sectionKey] : 0;
    const pagedRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    return (
      <Paper key={section.key} sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6'>{section.title}</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            Diese Rückerstattungen wurden vom Zahlungsdienstleister als fehlgeschlagen
            zurückgemeldet und müssen manuell nachverfolgt werden.
          </Typography>
        </Box>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Nr.</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell align='right'>Betrag</TableCell>
                <TableCell>Grund</TableCell>
                <TableCell>Fehler</TableCell>
                <TableCell>Standort</TableCell>
                <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(item => {
                const customer = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                return (
                  <TableRow key={`${section.key}-${item._id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.date ? dayjs(item.date).format('DD.MM.YYYY HH:mm') : '—'}
                    </TableCell>
                    <TableCell>
                      {item.bookingId && item.bookingNumber ? (
                        <Link
                          component='button'
                          variant='body2'
                          onClick={() =>
                            handleOpenDetails({ _id: item.bookingId, resourceId: item.resourceId })
                          }
                          sx={{ cursor: 'pointer', textDecoration: 'none' }}
                        >
                          {item.bookingNumber}
                        </Link>
                      ) : (
                        item.bookingNumber || '—'
                      )}
                    </TableCell>
                    <TableCell>{customer || item.buyerMail || '—'}</TableCell>
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                      {Number.isFinite(item.amount)
                        ? currencyFormatter.format(item.amount)
                        : '—'}
                    </TableCell>
                    <TableCell>{item.reason || '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={item.refundError || 'Keine Fehlermeldung übermittelt'}>
                        <Chip
                          size='small'
                          label='Fehlgeschlagen'
                          color='error'
                          sx={{ fontWeight: 700 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{item.branchId?.locationName || '—'}</TableCell>
                    <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                      <Stack
                        direction='row'
                        spacing={1}
                        justifyContent='flex-end'
                        alignItems='center'
                      >
                        {item.bookingId && (
                          <Tooltip title='Details öffnen'>
                            <IconButton
                              size='small'
                              onClick={() =>
                                handleOpenDetails({
                                  _id: item.bookingId,
                                  resourceId: item.resourceId,
                                })
                              }
                              sx={{ p: 0.5 }}
                            >
                              <OpenInNewIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasAcknowledgeRefundRight && (
                          <Button
                            size='small'
                            variant='contained'
                            color='secondary'
                            onClick={() => handleMarkRefundRead(item)}
                            sx={actionButtonSx}
                          >
                            <LockIconWrapper right='payment:special:acknowledgeFailedRefunds'>
                              Als gelesen markieren
                            </LockIconWrapper>
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={rows.length}
          page={page}
          onPageChange={(_, nextPage) =>
            setPageBySectionKey(prev => ({ ...prev, [sectionKey]: nextPage }))
          }
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    );
  };

  const renderCustomerHintsSection = section => {
    const rows = Array.isArray(section?.results) ? section.results : [];
    if (rows.length === 0) return null;

    const sectionKey = section.key;
    const page = Number.isFinite(pageBySectionKey?.[sectionKey]) ? pageBySectionKey[sectionKey] : 0;
    const pagedRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    return (
      <Paper key={section.key} sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6'>{section.title}</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            Diese Kunden haben über den Self-Service einen Hinweis fürs Team hinterlassen.
            Bitte lesen und anschließend als gelesen markieren — der Hinweis bleibt danach in
            der Buchung sichtbar und verschwindet nur aus dieser Liste.
          </Typography>
        </Box>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Nr.</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Termin</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Hinweis seit</TableCell>
                <TableCell sx={{ minWidth: 240 }}>Hinweis</TableCell>
                <TableCell>Standort</TableCell>
                <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(booking => (
                <TableRow key={`${section.key}-${booking._id}`}>
                  <TableCell>
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => handleOpenDetails(booking)}
                      sx={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {booking.bookingNumber}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')}
                  </TableCell>
                  <TableCell>
                    {booking.firstName} {booking.lastName}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {booking.customerCommentAt
                      ? dayjs(booking.customerCommentAt).format('DD.MM.YYYY HH:mm')
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {booking.customerComment || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{booking.branchId?.locationName || '—'}</TableCell>
                  <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0.75, sm: 1 },
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        flexWrap: 'wrap',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        minWidth: { sm: 'max-content' },
                      }}
                    >
                      <Tooltip title='Details öffnen'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenDetails(booking)}
                          sx={{ p: 0.5 }}
                        >
                          <OpenInNewIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      {hasHandleHintsRight && (
                        <Button
                          size='small'
                          variant='contained'
                          color='secondary'
                          onClick={() => handleMarkHintRead(booking)}
                          sx={actionButtonSx}
                        >
                          <LockIconWrapper right='internalBooking:special:handleCustomerComments'>
                            Als gelesen markieren
                          </LockIconWrapper>
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={rows.length}
          page={page}
          onPageChange={(_, nextPage) =>
            setPageBySectionKey(prev => ({ ...prev, [sectionKey]: nextPage }))
          }
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    );
  };

  const renderSection = section => {
    if (section?.key === 'autoCheckedOut') {
      return renderAutoCheckedOutSection(section);
    }

    if (section?.key === 'failedRefund') {
      return renderFailedRefundSection(section);
    }

    if (section?.key === 'customerHints') {
      return renderCustomerHintsSection(section);
    }

    const rows = Array.isArray(section?.results) ? section.results : [];
    if (rows.length === 0) return null;

    const sectionKey = section.key;
    const page = Number.isFinite(pageBySectionKey?.[sectionKey]) ? pageBySectionKey[sectionKey] : 0;
    const pagedRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    // The forgotten check-out is the one section where the slot's END is the fact that
    // matters — it says how long the guests have been counted as present.
    const showEndTime = sectionKey === 'missingCheckOut';

    return (
      <Paper key={section.key} sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6'>{section.title}</Typography>
        </Box>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Nr.</TableCell>
                <TableCell>Termin</TableCell>
                {showEndTime && <TableCell sx={{ whiteSpace: 'nowrap' }}>Ende</TableCell>}
                <TableCell>Kunde</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Entertainment</TableCell>
                <TableCell>Standort</TableCell>
                <TableCell>Ressource</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Rückmeldung bis</TableCell>
                <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(booking => (
                <TableRow key={`${section.key}-${booking._id}`}>
                  <TableCell>
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => handleOpenDetails(booking)}
                      sx={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      {booking.bookingNumber}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')}
                  </TableCell>
                  {showEndTime && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {booking.endTime
                        ? dayjs(booking.endTime).format('DD.MM.YYYY HH:mm')
                        : '—'}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <span>
                        {booking.firstName} {booking.lastName}
                      </span>
                      {renderSchoolClassChip(booking)}
                    </Stack>
                  </TableCell>
                  <TableCell>{renderAttentionChip(booking)}</TableCell>
                  <TableCell>{booking.entertainment}</TableCell>
                  <TableCell>{booking.branchId?.locationName || '—'}</TableCell>
                  <TableCell>{booking.resourceId?.name || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {booking.responseDeadline
                      ? dayjs(booking.responseDeadline).format('DD.MM.YYYY HH:mm')
                      : '—'}
                  </TableCell>
                  <TableCell align='right' sx={{ minWidth: { sm: 'max-content' } }}>
                    {renderRowActions(booking)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={rows.length}
          page={page}
          onPageChange={(_, nextPage) =>
            setPageBySectionKey(prev => ({ ...prev, [sectionKey]: nextPage }))
          }
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    );
  };

  const renderSkeletonSection = (key, rowCount) => (
    <Paper key={key} sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Skeleton variant='text' width={260} height={32} />
      </Box>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><Skeleton variant='text' width={40} /></TableCell>
              <TableCell><Skeleton variant='text' width={100} /></TableCell>
              <TableCell><Skeleton variant='text' width={90} /></TableCell>
              <TableCell><Skeleton variant='text' width={80} /></TableCell>
              <TableCell><Skeleton variant='text' width={110} /></TableCell>
              <TableCell><Skeleton variant='text' width={80} /></TableCell>
              <TableCell><Skeleton variant='text' width={90} /></TableCell>
              <TableCell align='right'><Skeleton variant='text' width={70} sx={{ ml: 'auto' }} /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, idx) => (
              <TableRow key={`${key}-skeleton-${idx}`}>
                <TableCell><Skeleton variant='text' width={60} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Skeleton variant='text' width={120} />
                </TableCell>
                <TableCell><Skeleton variant='text' width={140} /></TableCell>
                <TableCell>
                  <Skeleton variant='rounded' width={110} height={24} sx={{ borderRadius: 3 }} />
                </TableCell>
                <TableCell><Skeleton variant='text' width={120} /></TableCell>
                <TableCell><Skeleton variant='text' width={90} /></TableCell>
                <TableCell><Skeleton variant='text' width={110} /></TableCell>
                <TableCell align='right'>
                  <Box sx={{ display: 'inline-flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Skeleton variant='rounded' width={32} height={32} />
                    <Skeleton variant='rounded' width={100} height={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const visibleSections = sections.filter(
    section => Array.isArray(section?.results) && section.results.length > 0
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {hasAttentionShowAllRight && (
          <Tooltip title='Zeigt auch Buchungen vor dem Feature-Rollout an. Der Seitenzähler im Menü bleibt unbeeinflusst.'>
            <FormControlLabel
              control={
                <Switch
                  checked={showAll}
                  onChange={(_, checked) => setShowAll(checked)}
                  disabled={isLoading}
                />
              }
              label='Alle anzeigen (ohne Datumsgrenze)'
              sx={{ mr: 0 }}
            />
          </Tooltip>
        )}
        <Button variant='outlined' onClick={load} disabled={isLoading}>
          Aktualisieren
        </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Stack spacing={2} aria-busy='true' aria-live='polite'>
          {renderSkeletonSection('skeleton-a', 3)}
          {renderSkeletonSection('skeleton-b', 2)}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {visibleSections.length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Typography color='text.secondary'>Kein Handlungsbedarf vorhanden.</Typography>
            </Paper>
          ) : (
            visibleSections.map(renderSection)
          )}
        </Stack>
      )}

      <BookingDetailsModal
        open={detailsModal.open}
        onClose={handleCloseDetails}
        bookingId={detailsModal.bookingId}
        borderColor={detailsModal.borderColor}
        onUpdate={handleDetailsUpdate}
      />
    </Box>
  );
}
